// Iter56 — NewMaterialMovementModal
//
// Modal to request a new logistics / material movement. Standalone or linked
// to incident. If `prefill` prop is provided, fields are seeded (used by the
// sorting result form to chain a from_sorting request).
//
// POST /api/material-movements
import React, { useEffect, useState } from "react";
import axios from "axios";
import { useStore } from "../store";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DIRECTIONS = [
  { value: "to_sorting", label: "To sorting", desc: "Bring material to the sorting area" },
  { value: "from_sorting", label: "From sorting", desc: "Pick up sorted material from the sorting area" },
  { value: "between_locations", label: "Between locations", desc: "Move material between any two locations" },
];

const PRIORITIES = ["low", "medium", "high"];

export const NewMaterialMovementModal = ({ onClose, onCreated, prefill }) => {
  const addToast = useStore((s) => s.addToast);
  const [mode, setMode] = useState(prefill?.incident_id ? "linked" : "standalone");
  const [direction, setDirection] = useState(prefill?.direction || "to_sorting");
  const [fromLocation, setFromLocation] = useState(prefill?.from_location || "");
  const [toLocation, setToLocation] = useState(prefill?.to_location || "");
  const [partNumber, setPartNumber] = useState(prefill?.part_number || "");
  const [quantity, setQuantity] = useState(prefill?.quantity ?? "");
  const [note, setNote] = useState(prefill?.note || "");
  const [priority, setPriority] = useState(prefill?.priority || "medium");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const [incidents, setIncidents] = useState([]);
  const [search, setSearch] = useState("");
  const [pickedIncident, setPickedIncident] = useState(
    prefill?.incident_id ? { id: prefill.incident_id, title: prefill.incident_title || "" } : null,
  );

  useEffect(() => {
    if (mode !== "linked") return;
    axios.get(`${API}/incidents?status_in=open,investigating,escalated`)
      .then(({ data }) => setIncidents(Array.isArray(data) ? data : []))
      .catch(() => {});
  }, [mode]);

  const filtered = incidents
    .filter((i) => {
      const t = (i.title || "").toLowerCase();
      const id = (i.id || "").toLowerCase();
      const s = search.toLowerCase();
      return !s || t.includes(s) || id.includes(s);
    })
    .slice(0, 30);

  const submit = async () => {
    setError("");
    if (mode === "linked" && !pickedIncident) {
      setError("Pick an incident or switch to standalone");
      return;
    }
    if (direction === "between_locations" && (!fromLocation.trim() || !toLocation.trim())) {
      setError("Both from and to locations are required for 'between locations' direction");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        direction,
        from_location: fromLocation.trim() || null,
        to_location: toLocation.trim() || null,
        part_number: partNumber.trim() || null,
        quantity: quantity === "" || quantity == null ? null : parseInt(quantity, 10) || 0,
        note: note.trim() || null,
        priority,
        incident_id: mode === "linked" ? pickedIncident.id : null,
        sorting_result_id: prefill?.sorting_result_id || null,
      };
      const { data } = await axios.post(`${API}/material-movements`, payload);
      addToast({ type: "success", title: "Logistics request created", body: data.id });
      onCreated?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Submit failed";
      setError(typeof msg === "string" ? msg : JSON.stringify(msg));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center px-3"
      style={{ background: "rgba(0,0,0,0.65)", zIndex: 400 }}
      onClick={onClose}
      data-testid="material-movement-modal-overlay"
    >
      <div
        className="rounded-md p-4"
        style={{
          background: "var(--bg-surface, #0d0f18)",
          border: "1px solid rgba(255,255,255,0.18)",
          width: "min(680px, 96vw)", maxHeight: "92vh", overflowY: "auto",
        }}
        onClick={(e) => e.stopPropagation()}
        data-testid="material-movement-modal"
      >
        <div className="flex items-center justify-between mb-3">
          <span style={{ fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text, #f0f2f8)" }}>
            New Logistics Request
          </span>
          <button onClick={onClose} style={{ color: "#9aa1b3", fontSize: 22, cursor: "pointer", background: "transparent", border: "none" }} data-testid="material-movement-modal-close">×</button>
        </div>

        {!prefill?.sorting_result_id && (
          <div className="mb-3 flex gap-2">
            <ModePill active={mode === "standalone"} onClick={() => setMode("standalone")} testId="material-movement-mode-standalone">Standalone</ModePill>
            <ModePill active={mode === "linked"} onClick={() => setMode("linked")} testId="material-movement-mode-linked">Link to incident</ModePill>
          </div>
        )}

        {mode === "linked" && (
          <div className="mb-3 rounded-md p-2" style={{ border: "1px solid rgba(255,255,255,0.10)" }} data-testid="material-movement-incident-picker">
            {prefill?.incident_id ? (
              <div style={{ fontFamily: "DM Mono, monospace", fontSize: 11, color: "#9ec3ec", padding: "4px 0" }}>
                Linked to {prefill.incident_id} (from sorting workflow)
              </div>
            ) : (
              <>
                <input
                  type="text" placeholder="Search incidents…" value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full px-2 py-1.5 rounded outline-none mb-2"
                  style={inputStyle()}
                  data-testid="material-movement-incident-search"
                />
                <div className="max-h-40 overflow-y-auto space-y-1">
                  {filtered.map((inc) => (
                    <button
                      key={inc.id} type="button" onClick={() => setPickedIncident(inc)}
                      className="w-full text-left px-2 py-1.5 rounded"
                      style={{
                        background: pickedIncident?.id === inc.id ? "rgba(24,95,165,0.20)" : "var(--bg-base, #111420)",
                        border: pickedIncident?.id === inc.id ? "1px solid rgba(24,95,165,0.55)" : "1px solid rgba(255,255,255,0.08)",
                        cursor: "pointer",
                      }}
                      data-testid={`material-movement-incident-row-${inc.id}`}
                    >
                      <span style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9ec3ec" }}>{inc.id}</span>
                      <span className="ml-2" style={{ fontFamily: "Syne, sans-serif", fontSize: 12, color: "var(--text, #f0f2f8)" }}>{inc.title}</span>
                    </button>
                  ))}
                  {filtered.length === 0 && (
                    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 10, color: "#9198b0", padding: "8px 4px" }}>
                      No open incidents match the search.
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )}

        <Field label="Direction *">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2" data-testid="material-movement-direction-group">
            {DIRECTIONS.map((d) => (
              <button
                key={d.value} type="button" onClick={() => setDirection(d.value)}
                className="text-left p-2 rounded"
                style={{
                  background: direction === d.value ? "rgba(24,95,165,0.16)" : "var(--bg-base, #111420)",
                  border: `1px solid ${direction === d.value ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.10)"}`,
                  cursor: "pointer",
                }}
                data-testid={`material-movement-direction-${d.value}`}
              >
                <div style={{ fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, color: direction === d.value ? "#9ec3ec" : "var(--text, #f0f2f8)" }}>
                  {d.label}
                </div>
                <div style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", marginTop: 2 }}>{d.desc}</div>
              </button>
            ))}
          </div>
        </Field>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-3">
          <Field label={direction === "to_sorting" ? "From location *" : "From location"}>
            <input
              type="text" value={fromLocation} onChange={(e) => setFromLocation(e.target.value)}
              className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()}
              placeholder="Warehouse A / Rack 12"
              data-testid="material-movement-from-location"
            />
          </Field>
          <Field label={direction === "from_sorting" ? "To location *" : "To location"}>
            <input
              type="text" value={toLocation} onChange={(e) => setToLocation(e.target.value)}
              className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()}
              placeholder="Sorting bay / Dock 3"
              data-testid="material-movement-to-location"
            />
          </Field>
          <Field label="Part number">
            <input
              type="text" value={partNumber} onChange={(e) => setPartNumber(e.target.value)}
              className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()}
              placeholder="51 41 7 234 091"
              data-testid="material-movement-part-number"
            />
          </Field>
          <Field label="Quantity (pcs)">
            <input
              type="number" min="0" value={quantity} onChange={(e) => setQuantity(e.target.value)}
              className="w-full px-2 py-1.5 rounded outline-none" style={inputStyle()}
              placeholder="240"
              data-testid="material-movement-quantity"
            />
          </Field>
        </div>

        <Field label="Priority">
          <div className="flex gap-1.5" data-testid="material-movement-priority-group">
            {PRIORITIES.map((p) => (
              <button
                key={p} type="button" onClick={() => setPriority(p)}
                className="px-3 py-1.5 rounded"
                style={{
                  background: priority === p ? "rgba(24,95,165,0.16)" : "transparent",
                  border: `1px solid ${priority === p ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
                  color: priority === p ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
                  fontFamily: "DM Mono, monospace", fontSize: 10, letterSpacing: "0.5px",
                  cursor: "pointer", textTransform: "uppercase",
                }}
                data-testid={`material-movement-priority-${p}`}
              >
                {p}
              </button>
            ))}
          </div>
        </Field>

        <Field label="Note (optional)">
          <textarea
            value={note} onChange={(e) => setNote(e.target.value)} rows={2}
            className="w-full px-2 py-1.5 rounded outline-none"
            style={{ ...inputStyle(), resize: "vertical" }}
            placeholder="e.g. Lot LOT-23456 — handle with care"
            data-testid="material-movement-note"
          />
        </Field>

        {error && (
          <div className="mt-2 p-2 rounded" style={{ background: "rgba(226,75,74,0.08)", border: "1px solid rgba(226,75,74,0.35)", color: "#ff9a99", fontFamily: "DM Mono, monospace", fontSize: 11 }} data-testid="material-movement-modal-error">
            {error}
          </div>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button" onClick={onClose}
            className="px-3 py-2 rounded-md"
            style={{ background: "transparent", border: "1px solid rgba(255,255,255,0.14)", color: "#c5cad8", fontFamily: "Syne, sans-serif", fontWeight: 600, fontSize: 12, cursor: "pointer" }}
            data-testid="material-movement-modal-cancel"
          >
            Cancel
          </button>
          <button
            type="button" onClick={submit} disabled={busy}
            className="px-3 py-2 rounded-md"
            style={{ background: "#185FA5", color: "#fff", fontFamily: "Syne, sans-serif", fontWeight: 700, fontSize: 12, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.6 : 1, border: "none" }}
            data-testid="material-movement-modal-submit"
          >
            {busy ? "Submitting…" : "Submit request"}
          </button>
        </div>
      </div>
    </div>
  );
};

const ModePill = ({ active, onClick, children, testId }) => (
  <button
    type="button" onClick={onClick}
    className="px-3 py-1.5 rounded"
    style={{
      background: active ? "rgba(24,95,165,0.16)" : "transparent",
      border: `1px solid ${active ? "rgba(24,95,165,0.55)" : "rgba(255,255,255,0.12)"}`,
      color: active ? "#9ec3ec" : "var(--text-dim, #9aa1b3)",
      fontFamily: "DM Mono, monospace", fontSize: 11, letterSpacing: "0.5px",
      cursor: "pointer",
    }}
    data-testid={testId}
  >
    {children}
  </button>
);

const Field = ({ label, children }) => (
  <div className="mt-3">
    <div style={{ fontFamily: "DM Mono, monospace", fontSize: 9, color: "#9198b0", letterSpacing: "1.2px", textTransform: "uppercase", marginBottom: 4 }}>{label}</div>
    {children}
  </div>
);

const inputStyle = (fontSize = 12) => ({
  background: "var(--bg3, #08090d)",
  border: "1px solid rgba(255,255,255,0.10)",
  color: "var(--text, #f0f2f8)",
  fontFamily: "DM Mono, monospace",
  fontSize,
});
